import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Send, Clock, AlertCircle, CheckCircle, X, Ticket, Target } from 'lucide-react';
// Make sure the CSS import points to the correct, updated file name
import './EmailMarketing.css'; 

const API_URL = process.env.REACT_APP_API_URL;

const api = {
  getCampaignsOverview: async () => {
    const response = await fetch(`${API_URL}/api/email-templates/campaigns-overview`);
    if (!response.ok) {
      throw new Error('Failed to fetch campaign data');
    }
    return response.json();
  },
  getCampaignRecipients: async (action) => {
    const response = await fetch(`${API_URL}/api/email-templates/recipients/${action}`);
    if (!response.ok) throw new Error('Failed to fetch recipient data');
    return response.json();
  },
};

const ALLOWED_TEMPLATE_ACTIONS = [
    'new_coupon',
    'custom_campaign'
];

const EmailMarketing = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState({ campaignName: '', recipients: [] });
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setIsLoading(true);
        const data = await api.getCampaignsOverview();
        const filteredData = data.filter(campaign => ALLOWED_TEMPLATE_ACTIONS.includes(campaign.action));
        setCampaigns(filteredData);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching campaign overview:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  const handleNavigateToTemplates = () => navigate('/admin/emailtemplate');

  const handleOpenModal = async (campaign) => {
    setIsModalOpen(true);
    setIsModalLoading(true);
    setModalData({ campaignName: campaign.name, recipients: [] });
    setSearchQuery(''); 

    try {
        const recipients = await api.getCampaignRecipients(campaign.action);
        setModalData({ campaignName: campaign.name, recipients });
    } catch (err) {
        console.error("Error fetching recipients:", err);
    } finally {
        setIsModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSearchQuery('');
  }

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const getStatusInfo = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered': return { icon: <CheckCircle size={16} />, className: 'delivered' };
      case 'pending': return { icon: <Clock size={16} />, className: 'pending' };
      case 'failed': return { icon: <AlertCircle size={16} />, className: 'failed' };
      case 'partial success': return { icon: <Send size={16} />, className: 'partial-success' };
      default: return { icon: <Mail size={16} />, className: 'default' };
    }
  };

  const filteredRecipients = modalData.recipients.filter(recipient =>
    recipient.recipientEmail.toLowerCase().includes(searchQuery.toLowerCase())
  );

   const renderContent = () => {
    if (isLoading) {
      return <tr><td colSpan="5" className="em-dash-loading-cell">Loading campaign data...</td></tr>;
    }
    if (error) {
      return <tr><td colSpan="5" className="em-dash-error-cell">Error: {error}</td></tr>;
    }
    if (campaigns.length === 0) {
        return <tr><td colSpan="5" className="em-dash-loading-cell">No relevant campaign data found.</td></tr>;
    }
    return campaigns.map((campaign) => {
      const progressPercentage = campaign.total > 0 ? (campaign.delivered / campaign.total) * 100 : 0;
      const statusInfo = getStatusInfo(campaign.status);
      return (
        <tr key={campaign.id} className="em-dash-table-row">
          {/* FIX: Wrap content of each td in a div for controlled alignment */}
          <td>
            <div className="em-dash-cell-content em-dash-campaign-name-cell">
              {campaign.action === 'new_coupon' && <Ticket size={16} className="em-dash-campaign-icon" />}
              {campaign.action === 'custom_campaign' && <Target size={16} className="em-dash-campaign-icon" />}
              <span>{campaign.name}</span>
            </div>
          </td>
          <td>
            <div className="em-dash-cell-content em-dash-delivered-cell" onClick={() => handleOpenModal(campaign)}>
              {campaign.delivered.toLocaleString()}
            </div>
          </td>
          <td>
            <div className="em-dash-cell-content">
              <span className={`em-dash-status-badge ${statusInfo.className}`}>
                {statusInfo.icon} {campaign.status}
              </span>
            </div>
          </td>
          <td>
            <div className="em-dash-cell-content em-dash-progress-cell">
              <div className="em-dash-progress-bar-container">
                <div className="em-dash-progress-bar" style={{ width: `${progressPercentage}%` }}></div>
              </div>
              <span className="em-dash-progress-text">{campaign.delivered.toLocaleString()} / {campaign.total.toLocaleString()}</span>
            </div>
          </td>
          <td>
            <div className="em-dash-cell-content">
              {formatDateTime(campaign.sentAt)}
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <div className="em-dash-container">
       <div className="em-dash-header">
        <div className="em-dash-title"><Mail size={28} /><h2>Email Marketing Dashboard</h2></div>
        <button className="em-dash-template-btn" onClick={handleNavigateToTemplates}>Manage Templates</button>
      </div>

      <div className="em-dash-table-container">
        <table className="em-dash-table">
          <thead><tr><th>Email Name</th><th>Delivered</th><th>Status</th><th>Progress</th><th>Last Sent At</th></tr></thead>
          <tbody>{renderContent()}</tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="em-dash-modal-overlay" onClick={handleCloseModal}>
          <div className="em-dash-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="em-dash-modal-header">
              <h3>Recipients for "{modalData.campaignName}"</h3>
              <button className="em-dash-modal-close-btn" onClick={handleCloseModal}><X size={24} /></button>
            </div>
            <div className="em-dash-modal-body">
              {isModalLoading ? (
                <div className="em-dash-loading-cell">Loading recipients...</div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Search by email..."
                    className="em-dash-recipient-search-input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <ul className="em-dash-recipient-list">
                    {filteredRecipients.length > 0 ? (
                      filteredRecipients.map((recipient, index) => (
                        <li key={index} className="em-dash-recipient-item">
                          <span className="em-dash-recipient-email">{recipient.recipientEmail}</span>
                          <span className="em-dash-recipient-date">Sent on: {formatDateTime(recipient.sentAt)}</span>
                        </li>
                      ))
                    ) : (
                      <li className="em-dash-no-results-cell">No matching emails found.</li>
                    )}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailMarketing;
